import React, { useState } from 'react';
import { http } from '../../../lib/http';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/ui/table';
import { ShoppingCart, Plus, Trash2, Printer, X, Save } from 'lucide-react';

const EMPTY_ITEM = { name: '', quantity: '', unit: '' };

const ShoppingListPanel = ({ shoppingList, selectedDate, onRefresh }) => {
  const [editing, setEditing] = useState(false);
  const [items, setItems] = useState([{ ...EMPTY_ITEM }]);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const startCreate = () => {
    setItems([{ ...EMPTY_ITEM }]);
    setNotes('');
    setEditing(true);
  };

  const startEdit = () => {
    if (shoppingList && shoppingList.items) {
      setItems(shoppingList.items.map(i => ({ name: i.name, quantity: i.quantity, unit: i.unit || '' })));
      setNotes(shoppingList.notes || '');
    }
    setEditing(true);
  };

  const addRow = () => setItems([...items, { ...EMPTY_ITEM }]);

  const removeRow = (idx) => {
    if (items.length <= 1) return;
    setItems(items.filter((_, i) => i !== idx));
  };

  const updateItem = (idx, field, value) => {
    const updated = [...items];
    updated[idx] = { ...updated[idx], [field]: field === 'quantity' ? value : value };
    setItems(updated);
  };

  const handleSave = async () => {
    const validItems = items.filter(i => i.name.trim() && i.quantity);
    if (validItems.length === 0) return;

    setSaving(true);
    try {
      await http.post('/kitchen/shopping-list', {
        date: selectedDate,
        items: validItems.map(i => ({ name: i.name.trim(), quantity: Number(i.quantity), unit: i.unit.trim() })),
        notes: notes.trim(),
      });
      setEditing(false);
      onRefresh();
    } catch (err) {
      console.error('Failed to save shopping list:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (itemId) => {
    if (!shoppingList?._id) return;
    try {
      await http.put(`/kitchen/shopping-list/${shoppingList._id}/toggle-item`, { itemId });
      onRefresh();
    } catch (err) {
      console.error('Failed to toggle item:', err);
    }
  };

  const handleDelete = async () => {
    if (!shoppingList?._id) return;
    if (!window.confirm('Delete this shopping list?')) return;
    try {
      await http.delete(`/kitchen/shopping-list/${shoppingList._id}`);
      onRefresh();
    } catch (err) {
      console.error('Failed to delete shopping list:', err);
    }
  };

  const handlePrint = () => window.print();

  // ── Edit / Create form ──
  if (editing) {
    return (
      <Card className="border border-gray-200 shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold text-gray-800 flex items-center gap-2">
            <ShoppingCart className="h-5 w-5 text-orange-500" />
            {shoppingList ? 'Edit Shopping List' : 'Create Shopping List'}
            <Button variant="ghost" size="sm" className="ml-auto" onClick={() => setEditing(false)}>
              <X size={16} /> Cancel
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {items.map((item, idx) => (
              <div key={idx} className="flex gap-2 items-center">
                <Input
                  placeholder="Item name"
                  value={item.name}
                  onChange={(e) => updateItem(idx, 'name', e.target.value)}
                  className="flex-1"
                />
                <Input
                  type="number"
                  placeholder="Qty"
                  value={item.quantity}
                  onChange={(e) => updateItem(idx, 'quantity', e.target.value)}
                  className="w-24"
                />
                <Input
                  placeholder="Unit (kg, pcs...)"
                  value={item.unit}
                  onChange={(e) => updateItem(idx, 'unit', e.target.value)}
                  className="w-32"
                />
                <Button variant="ghost" size="sm" onClick={() => removeRow(idx)} disabled={items.length <= 1}>
                  <Trash2 size={14} className="text-red-400" />
                </Button>
              </div>
            ))}
          </div>

          <div className="mt-3">
            <Input
              placeholder="Notes (optional)"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>

          <div className="flex gap-2 mt-4">
            <Button variant="outline" size="sm" onClick={addRow}>
              <Plus size={14} className="mr-1" /> Add Item
            </Button>
            <Button
              size="sm"
              disabled={saving || items.every(i => !i.name.trim())}
              onClick={handleSave}
              className="bg-orange-500 hover:bg-orange-600 text-white"
            >
              <Save size={14} className="mr-1" /> {saving ? 'Saving...' : 'Save List'}
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // ── View mode ──
  return (
    <Card className="border border-gray-200 shadow-sm" id="printable-shopping-list">
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-semibold text-gray-800 flex items-center gap-2">
          <ShoppingCart className="h-5 w-5 text-orange-500" />
          Daily Shopping List
          <div className="ml-auto flex gap-2 print:hidden">
            {shoppingList ? (
              <>
                <Button variant="outline" size="sm" onClick={startEdit}>Edit</Button>
                <Button variant="outline" size="sm" onClick={handlePrint}>
                  <Printer size={14} className="mr-1" /> Print
                </Button>
                <Button variant="outline" size="sm" className="text-red-500 hover:text-red-700" onClick={handleDelete}>
                  <Trash2 size={14} className="mr-1" /> Delete
                </Button>
              </>
            ) : (
              <Button size="sm" onClick={startCreate} className="bg-orange-500 hover:bg-orange-600 text-white">
                <Plus size={14} className="mr-1" /> Create Shopping List
              </Button>
            )}
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {!shoppingList ? (
          <p className="text-center text-gray-400 py-6">No shopping list for this date.</p>
        ) : (
          <>
            {/* Print header (hidden on screen) */}
            <div className="hidden print:block mb-4">
              <h2 className="text-lg font-bold">Just Protein - Daily Shopping List</h2>
              <p className="text-sm text-gray-600">Date: {selectedDate}</p>
            </div>

            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10 print:hidden">Done</TableHead>
                  <TableHead>Ingredient</TableHead>
                  <TableHead className="text-right">Quantity</TableHead>
                  <TableHead>Unit</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {shoppingList.items.map((item) => (
                  <TableRow key={item._id} className={item.purchased ? 'opacity-50' : ''}>
                    <TableCell className="print:hidden">
                      <input
                        type="checkbox"
                        checked={item.purchased}
                        onChange={() => handleToggle(item._id)}
                        className="w-4 h-4 accent-orange-500"
                      />
                    </TableCell>
                    <TableCell className={`font-medium ${item.purchased ? 'line-through' : ''}`}>
                      {item.name}
                    </TableCell>
                    <TableCell className="text-right">{item.quantity}</TableCell>
                    <TableCell className="text-gray-500">{item.unit}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {shoppingList.notes && (
              <p className="mt-3 text-sm text-gray-600 bg-gray-50 rounded p-2">
                <span className="font-semibold">Notes:</span> {shoppingList.notes}
              </p>
            )}
          </>
        )}
      </CardContent>

      {/* Print styles */}
      <style>{`
        @media print {
          body * { visibility: hidden; }
          #printable-shopping-list, #printable-shopping-list * { visibility: visible; }
          #printable-shopping-list { position: absolute; left: 0; top: 0; width: 100%; }
          .print\\:hidden { display: none !important; }
          .hidden.print\\:block { display: block !important; visibility: visible !important; }
        }
      `}</style>
    </Card>
  );
};

export default ShoppingListPanel;
